import {
  calculateGrossMarginFromUnitPrice,
  calculateUnitPriceFromGrossMargin,
} from '@calculations/price/utilities/gross-margin';
import type { CalculationConfig } from '@calculations/types/calculation-config';
import { InitialMarginSource } from '@snap-types/models/snap-quote-line/columns';
import { TargetGrossMarginSource } from '@snap-types/models/snap-quote-price/price-calculation-results';
import type { Decimal } from '@snap-types/types/decimal';
import type { Nullish } from '@snap-types/types/nullish';

import { type TargetGrossMarginLastWin } from './types';

export type TargetGrossMarginCalculateParams = {
  quantity: Decimal;
  unitCost: Decimal;
  targetGrossMargin: Decimal;
  lastWin?: Nullish<TargetGrossMarginLastWin>;
};

export type TargetGrossMarginCalculateResult = {
  quantity: Decimal;
  unitCost: Decimal;
  unitPrice: Decimal;
  grossMargin: Decimal;
  grossMarginSource: TargetGrossMarginSource;
};

export function calculate(
  {
    quantity,
    unitCost,
    targetGrossMargin,
    lastWin,
  }: TargetGrossMarginCalculateParams,
  config: CalculationConfig,
): TargetGrossMarginCalculateResult {
  let grossMargin = targetGrossMargin;
  let grossMarginSource: TargetGrossMarginSource =
    InitialMarginSource.targetGrossMargin;

  if (lastWin && lastWin.grossMargin.gte(targetGrossMargin)) {
    grossMargin = lastWin.grossMargin;
    grossMarginSource = InitialMarginSource.lastWin;
  }

  let unitPrice = calculateUnitPriceFromGrossMargin(
    {
      unitCost,
      grossMargin,
    },
    config,
  );

  // If the LastWin unit price was higher for the same or a greater quantity,
  // use THAT instead of calculated based on target gross margin
  if (lastWinPriceAndQuantityGreater({ lastWin, unitPrice, quantity })) {
    unitPrice = lastWin!.unitPrice;
    grossMargin = calculateGrossMarginFromUnitPrice(
      { unitCost, unitPrice },
      config,
    );
    grossMarginSource = InitialMarginSource.calculatedFromLastWinUnitPrice;
  }

  return {
    grossMargin,
    grossMarginSource,
    quantity,
    unitCost,
    unitPrice,
  };
}

function lastWinPriceAndQuantityGreater({
  lastWin,
  unitPrice,
  quantity,
}: {
  lastWin?: TargetGrossMarginLastWin | null;
  unitPrice: Decimal;
  quantity: Decimal;
}) {
  return (
    lastWin &&
    lastWin.unitPrice.gte(unitPrice) &&
    lastWin.quantity.gte(quantity)
  );
}
